import { apiHttpOrigin } from "@/lib/env";

declare global {
  interface Window {
    pplOpenFile?: (url: string) => void;
  }
}

/* The app's web view ends its user agent with `PeopleAIAndroid/` or `PeopleAIiOS/` (older iPhone
   builds say Android too; apps/mobile/components/site-view.tsx). It cannot save a blob, print or
   open a second window; any other address opens in the app's in-app browser. */
const MARKERS = ["PeopleAIAndroid/", "PeopleAIiOS/"];

export function appUserAgent(userAgent: string | null | undefined): boolean {
  return MARKERS.some((marker) => userAgent?.includes(marker));
}

export function isInApp(): boolean {
  return typeof navigator !== "undefined" && appUserAgent(navigator.userAgent);
}

/** The iPhone app: payment for the service is never taken inside it (App Store 3.1.1). Older
 * builds carry the Android marker, so the device is read off the web view's own agent too. */
export function isInIosApp(): boolean {
  if (!isInApp()) return false;
  const agent = navigator.userAgent;
  return (
    agent.includes("PeopleAIiOS/") ||
    /iPhone|iPad|iPod/.test(agent) ||
    (/Macintosh/.test(agent) && navigator.maxTouchPoints > 1)
  );
}

/** A new tab on a computer, the in-app browser in the app. A file on this site needs the cookie
 * the in-app browser lacks, and the web view would reload it in place (Android loops), so it is
 * fetched here and handed over as a short link (`openSiteFile`). */
export function openLink(url: string, features = "noopener"): void {
  if (isInApp()) {
    if (onThisSite(url)) {
      void import("@/lib/files/download").then(({ openSiteFile }) => openSiteFile(url));
      return;
    }
    window.location.assign(url);
    return;
  }
  window.open(url, "_blank", features);
}

/** The app's web view hands a download or window.open on this site back here
 * (apps/mobile/components/site-view.tsx): reloading it in place loops on the iPhone. */
export function exposeFileHook(): void {
  if (!isInApp()) return;
  window.pplOpenFile = (url) => {
    if (onThisSite(url))
      void import("@/lib/files/download").then(({ openSiteFile }) => openSiteFile(url));
  };
}

function onThisSite(url: string): boolean {
  try {
    const target = new URL(url, window.location.href);
    return (
      target.origin === window.location.origin || target.origin === new URL(apiHttpOrigin).origin
    );
  } catch {
    return false;
  }
}
